import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

// Ortak Excel başlık stilini tanımlar
const getHeaderStyle = () => ({
  font: { bold: true, color: { argb: 'FFFFFFFF' } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
  border: {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const }
  }
});

// Ortak hücre stilini tanımlar
const getCellStyle = (rowNumber: number) => {
  const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
  return {
    fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
    border: {
      top: { style: 'thin' as const },
      left: { style: 'thin' as const },
      bottom: { style: 'thin' as const },
      right: { style: 'thin' as const }
    }
  };
};

// Haftalık günleri temsil eden dizi
const weekDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

// Ay isimlerini temsil eden dizi
const monthNames = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

// Gün adını alma yardımcı fonksiyonu
const getDayName = (dayNumber: number) => {
  const day = weekDayNames[dayNumber] || `Gün ${dayNumber}`;
  return day;
};

// Ay adını alma yardımcı fonksiyonu
const getMonthName = (monthNumber: number): string => {
  return monthNames[monthNumber] || `Ay ${monthNumber}`;
};

// Görevin günlerini ve ilgili saatlerini çıkarma fonksiyonu
const getTaskDays = (task: any) => {
  if (!task || !task.days) return [];
  
  return Object.keys(task.days).map(day => ({
    day: parseInt(day),
    dayName: getDayName(parseInt(day)),
    repetitionTimes: task.days[day].repetitionTimes || [],
    dailyRepetitions: task.days[day].dailyRepetitions || 1
  }));
};

// Görevi ay/gün şeklinde formatlanmış şekilde döndüren yardımcı fonksiyon
const getTaskMonthDays = (task: any) => {
  if (!task) return [];
  
  const result = [];
  
  // task içindeki ay bilgilerini döngüye alıyoruz
  for (const key in task) {
    if (key.startsWith('month')) {
      const monthNumber = parseInt(key.replace('month', ''));
      const monthData = task[key];
      const monthName = getMonthName(monthNumber);
      
      const days = [];
      
      // Ay içindeki günleri döngüye alıyoruz
      for (const dayKey in monthData) {
        if (dayKey.startsWith('day')) {
          const dayNumber = parseInt(dayKey.replace('day', ''));
          const dayData = monthData[dayKey];
          
          days.push({
            day: dayNumber,
            dailyRepetitions: dayData.dailyRepetitions || 1,
            repetitionTimes: dayData.repetitionTimes || []
          });
        }
      }
      
      if (days.length > 0) {
        // Günleri sayısal olarak sırala
        days.sort((a, b) => a.day - b.day);
        
        result.push({
          month: monthNumber,
          monthName,
          days
        });
      }
    }
  }
  
  // Ayları sayısal olarak sırala
  result.sort((a, b) => a.month - b.month);
  
  return result;
};

// Günlük görevleri Excel'e aktarma
export const exportDailyTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.daily'));
    
    // Sütun başlıklarını tanımla
    worksheet.columns = [
      { header: t('tasks.status.title'), key: 'status', width: 15 },
      { header: t('tasks.name'), key: 'name', width: 30 },
      { header: t('tasks.taskTimes'), key: 'times', width: 30 },
      { header: t('tasks.date'), key: 'date', width: 20 },
      { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
      { header: t('tasks.personnel'), key: 'personnel', width: 20 },
      { header: t('tasks.description'), key: 'description', width: 40 }
    ];
    
    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Görev saatleri
      const taskTimes = task.repetitionTimes && task.repetitionTimes.length > 0
        ? task.repetitionTimes.join(', ')
        : '-';
      
      // Tarih bilgisi
      let dateInfo = '-';
      if (task.status === 'completed') {
        dateInfo = task.fromDatabase && task.completionDate 
          ? task.completionDate 
          : (task.completedAt ? new Date(task.completedAt).toLocaleDateString(locale) : '-');
      } else if (task.status === 'missed') {
        dateInfo = task.fromDatabase && task.missedDate 
          ? task.missedDate 
          : (task.missedAt ? new Date(task.missedAt).toLocaleDateString(locale) : '-');
      }
      
      // Tolerans bilgisi
      const toleranceInfo = task.isRecurring ? `${task.startTolerance || 15} ${t('tasks.minutes')}` : '-';
      
      // Satır ekleme
      worksheet.addRow({
        status: getStatusLabel(task.status),
        name: task.name,
        times: taskTimes,
        date: dateInfo,
        tolerance: toleranceInfo,
        personnel: task.personnelName || t('tasks.unassigned'),
        description: task.description || ''
      });
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveAs(new Blob([buffer]), `${t('tasks.excel.fileNames.daily')}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Haftalık görevleri Excel'e aktarma
export const exportWeeklyTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.weekly'));

    // İlk görevi kontrol ederek durum filtresini belirle
    const isCompletedTasks = tasks.length > 0 && tasks[0].status === 'completed';
    const isMissedTasks = tasks.length > 0 && tasks[0].status === 'missed';
    
    // Durum filtresine göre sütunları tanımla
    if (isCompletedTasks) {
      // Tamamlanan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.startTime'), key: 'startTime', width: 20 },
        { header: t('tasks.endTime'), key: 'endTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else if (isMissedTasks) {
      // Kaçırılan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.missedTime'), key: 'missedTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else {
      // Normal görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 25 },
        { header: t('tasks.description'), key: 'description', width: 35 },
        { header: t('tasks.dueDate'), key: 'dueDate', width: 15 },
        { header: t('tasks.status.title'), key: 'status', width: 15 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.branch'), key: 'branch', width: 20 },
        { header: t('tasks.group'), key: 'group', width: 20 }
      ];
    }

    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Tolerans bilgisi
      const toleranceInfo = `${task.startTolerance || 15} ${t('tasks.minutes')}`;
      
      if (isCompletedTasks) {
        // Tamamlanan görevler için veri formatı
        // Tarih formatını düzenle
        const startTime = task.startedAt ? new Date(task.startedAt).toLocaleString(locale) : '-';
        const endTime = task.completedAt ? new Date(task.completedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          startTime: startTime,
          endTime: endTime,
          description: task.description || '-'
        });
      } else if (isMissedTasks) {
        // Kaçırılan görevler için veri formatı
        // Tarih formatını düzenle
        const missedTime = task.missedAt ? new Date(task.missedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          missedTime: missedTime,
          description: task.description || '-'
        });
      } else {
        // Normal görevler için veri formatı
        // Son tarihi formatla
        const dueDate = task.dueDate 
          ? new Date(task.dueDate).toLocaleDateString(locale) 
          : '-';
        
        // Satır ekleme
        worksheet.addRow({
          name: task.name || '-',
          description: task.description || '-',
          dueDate: dueDate,
          status: getStatusLabel(task.status),
          personnel: task.personnelName || t('tasks.unassigned'),
          branch: task.branchName || '-',
          group: task.groupName || '-'
        });
      }
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    let fileName = t('tasks.excel.fileNames.weekly');
    
    // Durum filtresine göre dosya adını düzenle
    if (isCompletedTasks) {
      fileName = `${t('tasks.excel.fileNames.completed')}_${t('tasks.excel.fileNames.weekly')}`;
    } else if (isMissedTasks) {
      fileName = `${t('tasks.excel.fileNames.missed')}_${t('tasks.excel.fileNames.weekly')}`;
    }
    
    saveAs(new Blob([buffer]), `${fileName}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Aylık görevleri Excel'e aktarma
export const exportMonthlyTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.monthly'));
    
    // İlk görevi kontrol ederek durum filtresini belirle
    const isCompletedTasks = tasks.length > 0 && tasks[0].status === 'completed';
    const isMissedTasks = tasks.length > 0 && tasks[0].status === 'missed';
    
    // Durum filtresine göre sütunları tanımla
    if (isCompletedTasks) {
      // Tamamlanan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.startTime'), key: 'startTime', width: 20 },
        { header: t('tasks.endTime'), key: 'endTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else if (isMissedTasks) {
      // Kaçırılan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.missedTime'), key: 'missedTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else {
      // Normal görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 25 },
        { header: t('tasks.description'), key: 'description', width: 35 },
        { header: t('tasks.daysAndTimes'), key: 'daysAndTimes', width: 40 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.branch'), key: 'branch', width: 20 },
        { header: t('tasks.group'), key: 'group', width: 20 }
      ];
    }
    
    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Tolerans bilgisi
      const toleranceInfo = `${task.startTolerance || 15} ${t('tasks.minutes')}`;
      
      if (isCompletedTasks) {
        // Tamamlanan görevler için veri formatı
        // Tarih formatını düzenle
        const startTime = task.startedAt ? new Date(task.startedAt).toLocaleString(locale) : '-';
        const endTime = task.completedAt ? new Date(task.completedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          startTime: startTime,
          endTime: endTime,
          description: task.description || '-'
        });
      } else if (isMissedTasks) {
        // Kaçırılan görevler için veri formatı
        // Tarih formatını düzenle
        const missedTime = task.missedAt ? new Date(task.missedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          missedTime: missedTime,
          description: task.description || '-'
        });
      } else {
        // Normal görevler için veri formatı
        // Günleri ve saatleri formatla
        const daysAndTimes = getTaskMonthDays(task);
        
        // Satır ekleme
        worksheet.addRow({
          name: task.name || '-',
          description: task.description || '-',
          daysAndTimes: daysAndTimes,
          personnel: task.personnelName || t('tasks.unassigned'),
          branch: task.branchName || '-',
          group: task.groupName || '-'
        });
      }
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
        
        // Sadece normal görevlerde daysAndTimes sütunu için metin sarma
        if (!isCompletedTasks && !isMissedTasks && row.getCell('daysAndTimes')) {
          row.getCell('daysAndTimes').alignment = {
            wrapText: true,
            vertical: 'top'
          };
          // Satır yüksekliğini ayarla
          row.height = 60;
        }
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    let fileName = t('tasks.excel.fileNames.monthly');
    
    // Durum filtresine göre dosya adını düzenle
    if (isCompletedTasks) {
      fileName = `${t('tasks.excel.fileNames.completed')}_${t('tasks.excel.fileNames.monthly')}`;
    } else if (isMissedTasks) {
      fileName = `${t('tasks.excel.fileNames.missed')}_${t('tasks.excel.fileNames.monthly')}`;
    }
    
    saveAs(new Blob([buffer]), `${fileName}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Yıllık görevleri Excel'e aktarma
export const exportYearlyTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.yearly'));
    
    // İlk görevi kontrol ederek durum filtresini belirle
    const isCompletedTasks = tasks.length > 0 && tasks[0].status === 'completed';
    const isMissedTasks = tasks.length > 0 && tasks[0].status === 'missed';
    
    // Durum filtresine göre sütunları tanımla
    if (isCompletedTasks) {
      // Tamamlanan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.startTime'), key: 'startTime', width: 20 },
        { header: t('tasks.endTime'), key: 'endTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else if (isMissedTasks) {
      // Kaçırılan görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 30 },
        { header: t('tasks.taskDay'), key: 'taskDay', width: 20 },
        { header: t('tasks.taskTime'), key: 'taskTime', width: 15 },
        { header: t('tasks.tolerance'), key: 'tolerance', width: 12 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.missedTime'), key: 'missedTime', width: 20 },
        { header: t('tasks.description'), key: 'description', width: 30 }
      ];
    } else {
      // Normal görevler için sütunlar
      worksheet.columns = [
        { header: t('tasks.name'), key: 'name', width: 25 },
        { header: t('tasks.description'), key: 'description', width: 35 },
        { header: t('tasks.planDate'), key: 'planDate', width: 20 },
        { header: t('tasks.personnel'), key: 'personnel', width: 20 },
        { header: t('tasks.branch'), key: 'branch', width: 20 },
        { header: t('tasks.group'), key: 'group', width: 20 },
        { header: t('tasks.yearlyPlanDetails'), key: 'details', width: 40 }
      ];
    }
    
    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Tolerans bilgisi
      const toleranceInfo = `${task.startTolerance || 15} ${t('tasks.minutes')}`;
      
      if (isCompletedTasks) {
        // Tamamlanan görevler için veri formatı
        // Tarih formatını düzenle
        const startTime = task.startedAt ? new Date(task.startedAt).toLocaleString(locale) : '-';
        const endTime = task.completedAt ? new Date(task.completedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          startTime: startTime,
          endTime: endTime,
          description: task.description || '-'
        });
      } else if (isMissedTasks) {
        // Kaçırılan görevler için veri formatı
        // Tarih formatını düzenle
        const missedTime = task.missedAt ? new Date(task.missedAt).toLocaleString(locale) : '-';
        const taskDate = task.taskDate || '-'; // Görev günü
        
        worksheet.addRow({
          name: task.name || '-',
          taskDay: taskDate,
          taskTime: task.taskTime || '-',
          tolerance: toleranceInfo,
          personnel: task.personnelName || t('tasks.unassigned'),
          missedTime: missedTime,
          description: task.description || '-'
        });
      } else {
        // Normal görevler için veri formatı
        // Planlama tarihi
        const planDate = task.planDate 
          ? new Date(task.planDate).toLocaleDateString(locale) 
          : '-';
        
        // Satır ekleme
        worksheet.addRow({
          name: task.name || '-',
          description: task.description || '-',
          planDate: planDate,
          personnel: task.personnelName || t('tasks.unassigned'),
          branch: task.branchName || '-',
          group: task.groupName || '-',
          details: task.yearlyPlanDetails || '-'
        });
      }
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
        
        // Normal görevler için metin sarma
        if (!isCompletedTasks && !isMissedTasks) {
          // Görev açıklaması ve detaylar için metin sarma
          if (row.getCell('description')) {
            row.getCell('description').alignment = {
              wrapText: true,
              vertical: 'top'
            };
          }
          
          if (row.getCell('details')) {
            row.getCell('details').alignment = {
              wrapText: true,
              vertical: 'top'
            };
          }
          
          // Satır yüksekliğini ayarla
          row.height = 60;
        }
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    let fileName = t('tasks.excel.fileNames.yearly');
    
    // Durum filtresine göre dosya adını düzenle
    if (isCompletedTasks) {
      fileName = `${t('tasks.excel.fileNames.completed')}_${t('tasks.excel.fileNames.yearly')}`;
    } else if (isMissedTasks) {
      fileName = `${t('tasks.excel.fileNames.missed')}_${t('tasks.excel.fileNames.yearly')}`;
    }
    
    saveAs(new Blob([buffer]), `${fileName}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Normal görevleri Excel'e aktarma
export const exportNormalTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.normal'));
    
    // Sütun başlıklarını tanımla
    worksheet.columns = [
      { header: t('tasks.status.title'), key: 'status', width: 15 },
      { header: t('tasks.name'), key: 'name', width: 30 },
      { header: t('tasks.description'), key: 'description', width: 40 },
      { header: t('tasks.dueDate'), key: 'dueDate', width: 15 },
      { header: t('tasks.personnel'), key: 'personnel', width: 20 },
      { header: t('tasks.branch'), key: 'branch', width: 20 },
      { header: t('tasks.group'), key: 'group', width: 20 }
    ];
    
    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Son tarihi formatla
      const dueDate = task.dueDate 
        ? new Date(task.dueDate).toLocaleDateString(locale) 
        : '-';
      
      // Satır ekleme
      worksheet.addRow({
        status: getStatusLabel(task.status),
        name: task.name || '-',
        description: task.description || '-',
        dueDate: dueDate,
        personnel: task.personnelName || t('tasks.unassigned'),
        branch: task.branchName || '-',
        group: task.groupName || '-'
      });
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
        
        // Görev açıklaması için metin sarma
        if (row.getCell('description')) {
          row.getCell('description').alignment = {
            wrapText: true,
            vertical: 'top'
          };
        }
        
        // Satır yüksekliğini ayarla
        row.height = 40;
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveAs(new Blob([buffer]), `${t('tasks.excel.fileNames.normal')}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Ekip görevlerini Excel'e aktarma
export const exportTeamTasksToExcel = async (
  tasks: any[],
  getStatusLabel: (status: string) => string,
  t: any,
  locale: string
) => {
  try {
    // Excel workbook oluştur
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('tasks.excel.fileNames.team'));
    
    // Sütun başlıklarını tanımla
    worksheet.columns = [
      { header: t('tasks.status.title'), key: 'status', width: 15 },
      { header: t('tasks.name'), key: 'name', width: 30 },
      { header: t('tasks.description'), key: 'description', width: 40 },
      { header: t('tasks.dueDate'), key: 'dueDate', width: 15 },
      { header: t('tasks.teamLabel'), key: 'team', width: 20 },
      { header: t('tasks.branch'), key: 'branch', width: 20 },
      { header: t('tasks.assignedPersonnel'), key: 'assignedPersonnel', width: 30 }
    ];
    
    // Başlık stilini uygula
    worksheet.getRow(1).eachCell(cell => {
      cell.style = getHeaderStyle();
    });
    
    // Görev verilerini ekle
    tasks.forEach(task => {
      // Son tarihi formatla
      const dueDate = task.dueDate 
        ? new Date(task.dueDate).toLocaleDateString(locale) 
        : '-';
      
      // Satır ekleme
      worksheet.addRow({
        status: getStatusLabel(task.status),
        name: task.name || '-',
        description: task.description || '-',
        dueDate: dueDate,
        team: task.teamName || '-',
        branch: task.branchName || '-',
        assignedPersonnel: Array.isArray(task.assignedPersonnel) 
          ? task.assignedPersonnel.join(', ') 
          : (typeof task.assignedPersonnel === 'string' ? task.assignedPersonnel : '-')
      });
    });
    
    // Zebrastripe (alternatif satır renklendirme)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // başlığı atla
        row.eachCell(cell => {
          cell.style = getCellStyle(rowNumber);
        });
        
        // Görev açıklaması için metin sarma
        if (row.getCell('description')) {
          row.getCell('description').alignment = {
            wrapText: true,
            vertical: 'top'
          };
        }
        
        // Atanan personel için metin sarma
        if (row.getCell('assignedPersonnel')) {
          row.getCell('assignedPersonnel').alignment = {
            wrapText: true,
            vertical: 'top'
          };
        }
        
        // Satır yüksekliğini ayarla
        row.height = 40;
      }
    });
    
    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveAs(new Blob([buffer]), `${t('tasks.excel.fileNames.team')}_${currentDate}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Excel dışa aktarma hatası:', error);
    return false;
  }
};

// Excel butonunu oluşturan bileşen
export const ExcelExportButton = ({
  tasks,
  taskType,
  loading,
  getStatusLabel
}: {
  tasks: any[];
  taskType: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'normal' | 'takvim' | 'team';
  loading: boolean;
  getStatusLabel: (status: string) => string;
}) => {
  // Translation hook'unu ekle
  const { t, i18n } = useTranslation('common2');
  const locale = i18n.language === 'en' ? 'en-US' : 'tr-TR';
  
  // Excel'e aktarma fonksiyonu
  const handleExport = async () => {
    try {
      console.log(`Exporting ${taskType} tasks. Total tasks: ${tasks.length}`); // Debug için log
      
      let result = false;
      
      switch (taskType) {
        case 'daily':
          result = await exportDailyTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'weekly':
          result = await exportWeeklyTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'monthly':
          result = await exportMonthlyTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'yearly':
          result = await exportYearlyTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'normal':
          result = await exportNormalTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'team':
          result = await exportTeamTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        case 'takvim':
          // Takvim görünümünde normal görevleri kullan
          result = await exportNormalTasksToExcel(tasks, getStatusLabel, t, locale);
          break;
        default:
          console.error(`Bilinmeyen görev tipi: ${taskType}`);
          alert(t('tasks.excel.unknownTaskType'));
          return;
      }
      
      if (!result) {
        alert(t('tasks.excel.exportError'));
      }
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      alert(t('tasks.excel.exportError'));
    }
  };
  
  return (
    <Button
      variant="contained"
      color="info"
      startIcon={<DownloadIcon />}
      onClick={handleExport}
      disabled={loading || tasks.length === 0}
      size="small"
    >
      {t('tasks.buttons.exportExcel')}
    </Button>
  );
}; 